/* ==========================================================================
   TRFU accessibility runtime

   Single job: keep --trfu-header-h equal to the real rendered height of the
   sticky header, so that scroll-padding-top and scroll-margin-top in
   trfu-a11y.css land in page anchors below it rather than behind it.

   The header is two stacked rows (the bar and the fixture strip) and both are
   inside the sticky wrapper, so the height changes with viewport width and
   with whether the strip has any fixtures left to show. Measuring beats
   hard coding.
   ========================================================================== */
(function () {
  'use strict';

  var docEl = document.documentElement;
  var FALLBACK = 121;
  var last = null;
  var motionQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  function stickyHeader() {
    return (
      document.querySelector('.section-header') ||
      document.querySelector('.shopify-section-group-header-group') ||
      null
    );
  }

  function measure() {
    var el = stickyHeader();
    if (!el) return;

    var height = Math.round(el.getBoundingClientRect().height);

    /* Guard against a mid transition measurement of zero, and against a
       runaway value if the header is ever rendered unstuck at full height. */
    if (!height || height < 24 || height > 400) height = FALLBACK;
    if (height === last) return;

    last = height;
    docEl.style.setProperty('--trfu-header-h', height + 'px');
  }

  function watch() {
    measure();

    var el = stickyHeader();
    if (el && typeof window.ResizeObserver === 'function') {
      new window.ResizeObserver(measure).observe(el);
    }

    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('orientationchange', measure, { passive: true });
    window.addEventListener('load', measure);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }

  /* Theme editor: the header section can be re-rendered under us. */
  document.addEventListener('shopify:section:load', function () {
    last = null;
    watch();
  });
  document.addEventListener('shopify:section:unload', function () {
    last = null;
    measure();
  });

  /* ========================================================================
     2. "Enquire Now" no longer rescales the viewport
     WCAG 2.4.3 Focus Order (A), 1.4.4 Resize Text (AA)

     hospitality-hub, Cockies-club and sentinels each ship the same handler:

       e.preventDefault();
       target.scrollIntoView({ behavior: ..., block: 'start' });
       var firstInput = target.querySelector('input, select, textarea, button');
       if (firstInput) setTimeout(function(){ firstInput.focus(...) }, 600);

     Two defects. First, the first match in document order is the hidden
     <input name="form_type">, which is not focusable, so focus was silently
     lost and preventDefault had already cancelled the browser's own fragment
     focus. Second, on any field it did reach, focusing a sub-16px input made
     iOS Safari zoom the page with no user action at all, and iOS never zooms
     back out.

     This listener runs in the capture phase on document and stops propagation,
     so the section handler never sees the click. Focus lands on the form
     heading instead, which announces where the user has arrived and cannot
     trigger a zoom.
     ======================================================================== */
  document.addEventListener('click', function (e) {
    var link = e.target && e.target.closest ? e.target.closest('a[data-trfu-scroll="true"]') : null;
    if (!link) return;

    var href = link.getAttribute('href') || '';
    if (href.charAt(0) !== '#') return;

    var target = document.getElementById(href.slice(1));
    if (!target) return;

    e.preventDefault();
    e.stopPropagation();

    var reduce = motionQuery ? motionQuery.matches : false;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });

    var landing = target.querySelector('h1, h2, h3') || target;
    landing.setAttribute('tabindex', '-1');
    landing.focus({ preventScroll: true });

    if (history.replaceState) history.replaceState(null, '', href);
  }, true);

  /* ========================================================================
     3. Filter rows that declared a tab pattern they never implemented
     WCAG 4.1.2 Name Role Value, Level A

     sections/downer-academy.liquid renders role="tablist" with role="tab"
     children, no tabpanel, no aria-controls and no arrow key handling, so a
     screen reader announces "tab, All, selected, 1 of 5" and then finds
     nothing the tab controls. They are not tabs, they filter a grid in place.

     The same defect on sections/player-grid.liquid and the homepage hero is
     fixed in the Liquid itself. This handles the one remaining section from
     here, because the filters only work with JavaScript anyway: with no JS
     there is no filtering, so there is nothing for the roles to misdescribe.
     ======================================================================== */
  function repairFilterGroup(group) {
    if (group.hasAttribute('data-trfu-aria-fixed')) return;
    group.setAttribute('data-trfu-aria-fixed', '1');
    group.setAttribute('role', 'group');

    var chips = Array.prototype.slice.call(group.querySelectorAll('[role="tab"]'));
    if (!chips.length) return;

    var status = document.createElement('p');
    status.className = 'trfu-sr-only';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    if (group.parentNode) group.parentNode.insertBefore(status, group.nextSibling);

    function sync(announce) {
      chips.forEach(function (chip) {
        chip.removeAttribute('role');
        chip.removeAttribute('aria-selected');
        var on = chip.classList.contains('is-active');
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on && announce) {
          var grid = document.querySelector('.da-grid');
          var shown = grid
            ? grid.querySelectorAll('.da-athlete:not([hidden])').length
            : 0;
          status.textContent = shown + (shown === 1 ? ' athlete' : ' athletes') +
            ' shown for ' + chip.textContent.trim() + '.';
        }
      });
    }

    sync(false);
    chips.forEach(function (chip) {
      /* Runs after the section's own handler has set is-active. */
      chip.addEventListener('click', function () {
        window.setTimeout(function () { sync(true); }, 0);
      });
    });
  }

  function repairAll() {
    Array.prototype.slice
      .call(document.querySelectorAll('.da-filters[role="tablist"]'))
      .forEach(repairFilterGroup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', repairAll);
  } else {
    repairAll();
  }
  document.addEventListener('shopify:section:load', repairAll);
}());
